import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';
import {
  getOrgSetting,
  upsertOrgSetting as upsertOrgSettingApi,
  deleteOrgSetting as deleteOrgSettingApi,
  type UpsertOrgSettingOptions,
} from '@kirin/api-client/api/settings';
import type {
  SettingResponse,
  SettingTestResult,
  SettingTypeValue,
  UpsertSettingBody,
} from '@kirin/api-client/contracts/settings';

/** Variables for upsert mutation: body and optional query params (e.g. test=only for SIEM). */
export type UpsertOrgSettingVariables = {
  body: UpsertSettingBody;
  params?: Record<string, string>;
};

/**
 * Hook to fetch an organization setting by type.
 *
 * The backend returns a virtual default (HTTP 200) for setting types that have
 * one — currently `EnforcementSeverityThreshold` (`{ severity: 'high' }`) and
 * `DataCollection`. For setting types without a virtual default (currently
 * only `Siem`), the backend still responds with 404 when no row exists, which
 * surfaces as `query.isError` with a `NotFoundError`.
 *
 * Authorization: Owner or Manager only.
 */
export function useOrgSetting(
  organizationId: string,
  settingType: SettingTypeValue,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.orgSettings.detail(organizationId, settingType),
    queryFn: () => getOrgSetting(apiClient, organizationId, settingType),
    enabled: enabled && !!organizationId && !!settingType,
    retry: retryHandlers.detail,
  });
}

/**
 * Hook to create or update an organization setting by type.
 * Accepts optional query params (e.g. params: { test: 'only' } for connection test only).
 * Only updates the query cache when a full setting was persisted (not when e.g. test=only).
 *
 * Authorization: Owner or Manager only.
 */
export function useUpsertOrgSetting(
  organizationId: string,
  settingType: SettingTypeValue
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: UpsertOrgSettingVariables) => {
      if (!organizationId) {
        return Promise.reject(
          new Error(
            'Cannot upsert organization setting: organizationId is required'
          )
        );
      }
      const options: UpsertOrgSettingOptions | undefined = variables.params
        ? { params: variables.params }
        : undefined;
      return upsertOrgSettingApi(
        apiClient,
        organizationId,
        settingType,
        variables.body,
        options
      );
    },
    onSuccess: (
      data: SettingResponse | SettingTestResult,
      variables: UpsertOrgSettingVariables
    ) => {
      if (variables.params?.test === 'only') {
        return;
      }
      queryClient.setQueryData<SettingResponse>(
        queryKeys.orgSettings.detail(organizationId, settingType),
        old =>
          old
            ? { ...old, ...(data as SettingResponse) }
            : (data as SettingResponse)
      );
    },
  });
}

/**
 * Hook to delete (disconnect) an organization setting by type.
 * Invalidates the setting query cache on success.
 *
 * Authorization: Owner or Manager only.
 */
export function useDeleteOrgSetting(
  organizationId: string,
  settingType: SettingTypeValue
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!organizationId) {
        return Promise.reject(
          new Error(
            'Cannot delete organization setting: organizationId is required'
          )
        );
      }
      return deleteOrgSettingApi(apiClient, organizationId, settingType);
    },
    onSuccess: () => {
      queryClient.setQueryData(
        queryKeys.orgSettings.detail(organizationId, settingType),
        null
      );
    },
  });
}
